import React, { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { Text } from '@stashinvest/react-components';
import { ArrowSquareOutIcon } from '@stashinvest/react-assets';
import { UserAvatar } from 'stash/components/global/UserAvatar';
import { getSettingsPages } from 'stash/components/settings/SettingRoutes';
import {
	getDisabledFeatures,
	selectAdvisoryAgreement,
	selectIsContentMoat,
} from 'stash/selectors';
import { getAdvisorySignature } from 'stash/actions/api/advisoryAgreement';
import { getFormattedDateString, parseDate } from 'stash/utils/time';
import { useIsPaywalled } from '../north-star-home/utils/utils';
import { EXPERIMENTS, useIsInExperimentVariation } from 'stash/components/ab-testing';
import StashWorksBanner from '../banners/stash-works/StashWorksBanner';
import { Origin, Screen } from '@stashinvest/product-events-typescript';
import {
	trackFinancialProfileQuestionsEntryClicked,
	trackFinancialProfileQuestionsEntryViewed,
} from 'stash/components/financial-profile/financialProfileQuestionsEntryEvents';
import useTrack from 'stash/hooks/useTrack';
import { useUnreadContentCardCount } from 'stash/components/notification-center';

type DrawerUser = {
	first_name?: string;
	last_name?: string;
	created_at?: string | Date;
};

type DrawerProps = {
	active: boolean;
	close: () => void;
	logOut: () => void;
	user: DrawerUser;
	avatar?: string;
};

type DrawerHeaderProps = {
	avatar?: string;
	user: DrawerUser;
	isContentMoat?: boolean;
};

type LogOutItemProps = {
	logOut: () => void;
};

type DrawerItemProps = {
	close: () => void;
	onItemClick?: () => void;
	pathname: string;
	title: React.ReactNode;
};

const DrawerHeader = ({ avatar, user, isContentMoat }: DrawerHeaderProps) => {
	const parsedCreatedAt = parseDate(user.created_at);

	return (
		<div className="border-b border-outlineVariant p-contentMediumSmall text-right">
			<div className="flex items-center justify-end gap-content1_5XS">
				<div className="font-bold">
					<Text
						tag="h3"
						textStyle="headerSmall"
						color="text-onSurface"
						textAlign="right"
					>
						{`${user.first_name} ${user.last_name}`}
					</Text>
					<Text
						tag="h4"
						textStyle="bodyMediumEmphasize"
						color="text-onSurface"
						textAlign="right"
					>
						Stasher since{' '}
						{parsedCreatedAt
							? getFormattedDateString(parsedCreatedAt, 'MM/yyyy')
							: ''}
					</Text>
				</div>
				<UserAvatar
					imageUrl={avatar}
					firstName={user.first_name}
					size="large"
					className="shrink-0 border-2 border-outlineVariant"
				/>
			</div>
			{!isContentMoat && (
				<div className="mt-content2XS block gridMedium:hidden">
					<StashWorksBanner />
				</div>
			)}
		</div>
	);
};

const LogOutItem = ({ logOut }: LogOutItemProps) => (
	<div>
		<button
			type="button"
			onClick={logOut}
			className="block w-full px-contentMedium py-contentXS"
		>
			<span className="flex items-center justify-end gap-content3XS">
				<Text tag="span" textStyle="bodyMediumStandard" color="text-error">
					Log out
				</Text>
				<ArrowSquareOutIcon size="2XS" color="text-error" />
			</span>
		</button>
	</div>
);

const DrawerItem = ({ close, onItemClick, pathname, title }: DrawerItemProps) => {
	const isReferral = pathname.indexOf('referrals') > -1;
	// Settings entries (e.g. ATM Locator) can be absolute URLs, which must
	// navigate via a real anchor rather than in-app router routing.
	const isExternal = /^(https?:)?\/\//.test(pathname);
	const linkClassName = 'block px-contentMedium py-contentXS text-right no-underline';

	const handleClick = () => {
		onItemClick?.();
		close();
	};

	const label = (
		<Text
			tag="span"
			textStyle={isReferral ? 'bodyMediumEmphasize' : 'bodyMediumStandard'}
			color="text-onSurfaceVariant"
		>
			{title}
		</Text>
	);

	return (
		<div>
			{isExternal ? (
				<a
					href={pathname}
					target="_blank"
					rel="noopener noreferrer"
					onClick={handleClick}
					className={linkClassName}
				>
					{label}
				</a>
			) : (
				<Link
					to={{
						pathname,
						state: { origin: Origin.userProfile },
					}}
					onClick={handleClick}
					className={linkClassName}
				>
					{label}
				</Link>
			)}
		</div>
	);
};

const Drawer = ({ active, close, logOut, user, avatar }: DrawerProps) => {
	const dispatch = useDispatch();
	const isPaywalled = useIsPaywalled();
	const disabledFeatures = useSelector(getDisabledFeatures);
	const isContentMoat = useSelector(selectIsContentMoat);
	const hasAdvisorySignature = useSelector(selectAdvisoryAgreement);
	const isMarketInsightsEnabled = useIsInExperimentVariation(
		EXPERIMENTS.IS_MARKET_INSIGHTS_REPORT_ENABLED,
		true
	);
	const isNotificationCenterEnabled = useIsInExperimentVariation(
		EXPERIMENTS.IS_NOTIFICATION_CENTER_ENABLED,
		true
	);
	const unreadNotificationCount = useUnreadContentCardCount(isNotificationCenterEnabled);
	const track = useTrack();
	const hasTrackedDrawerEntryViewRef = useRef(false);

	useEffect(() => {
		dispatch(getAdvisorySignature());
	}, [dispatch]);

	useEffect(() => {
		if (!active || hasTrackedDrawerEntryViewRef.current) {
			return;
		}

		hasTrackedDrawerEntryViewRef.current = true;
		trackFinancialProfileQuestionsEntryViewed(track, {
			screen: Screen.financialProfile,
			origin: Origin.userProfile,
		});
	}, [active, track]);

	const pages = getSettingsPages({
		disabledFeatures,
		archive: /archive/.test(window.location.href) ? '/archive' : '',
		isContentMoat,
		hasAdvisorySignature,
		isPaywalled,
		isMarketInsightsEnabled,
		isNotificationCenterEnabled,
		hasUnreadNotifications: unreadNotificationCount > 0,
	});

	return (
		<div>
			<button
				type="button"
				aria-label="Close menu"
				onClick={close}
				className={`fixed inset-0 z-[14] h-full w-full border-0 bg-scrim/opacityScrim p-0 ${
					active
						? 'visible opacity-[1] [transition:opacity_0.15s,_visibility_0s_0s]'
						: 'invisible opacity-[0] [transition:opacity_0.3s,_visibility_0.3s_0.3s]'
				}`}
			/>
			<nav
				className={`fixed right-0 top-0 z-[14] block h-full w-4/5 max-w-[25rem] overflow-y-scroll bg-background transition-transform duration-[400ms] [-webkit-overflow-scrolling:touch] ${
					active ? 'translate-x-0' : 'translate-x-full'
				}`}
			>
				<DrawerHeader user={user} avatar={avatar} isContentMoat={isContentMoat} />
				{pages.map((page) => (
					<DrawerItem
						key={page.pathname}
						title={page.title}
						pathname={page.pathname}
						close={close}
						onItemClick={
							page.pathname === '/investor-profile'
								? () =>
										trackFinancialProfileQuestionsEntryClicked(track, {
											screen: Screen.financialProfile,
											origin: Origin.userProfile,
										})
								: undefined
						}
					/>
				))}
				<LogOutItem logOut={logOut} />
			</nav>
		</div>
	);
};

export default Drawer;
